import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { switchMap, takeUntil, tap } from 'rxjs/operators';
import { TranslatePipe } from '@ngx-translate/core';
import { IOrderInformation, SdOrderInformationComponent } from '@seb/web-design-system/account';
import {
  BrandThemesEnum,
  SdBannerComponent, SdBorderWrapperComponent,
  SdListComponent,
  SdLoaderComponent,
  SdTitleComponent
} from '@seb/web-design-system/core';
import { CustomOrder } from '@seb/web-common/core/models';
import { OrderStatusEnum } from '@seb/web-common/core/enums';
import { OrderMapper } from '@seb/web-common/core/mappers';
import { MyAccountService } from '@seb/web-common/core/services';
import { OrderFacade } from '@seb/web-common/core/facades';

@Component({
  selector: 'wc-my-account-dashboard',
  templateUrl: './my-account-dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdTitleComponent,
    SdBorderWrapperComponent,
    SdBannerComponent,
    SdListComponent,
    SdOrderInformationComponent,
    SdLoaderComponent,
    TranslatePipe
  ]
})
export class MyAccountDashboardComponent implements OnInit, OnDestroy {
  urlImage = '/assets/images/dashboard-brands.png';
  userName = '';
  brandThemesEnum = BrandThemesEnum;
  orders: IOrderInformation;
  isLoading = true;

  currentOrders: IOrderInformation[];
  protected readonly destroy$: Subject<void> = new Subject();

  private readonly LAST_ORDER_STATUSES = [
    OrderStatusEnum.IN_PREPARATION,
    OrderStatusEnum.VALIDATED,
    OrderStatusEnum.PARTIALLY_DISPATCH,
    OrderStatusEnum.SHIPPED,
    OrderStatusEnum.DELETED,
    OrderStatusEnum.CANCELED,
    OrderStatusEnum.CANCELLED,
    OrderStatusEnum.PARTIALLY_SHIPPED_CANCELLED
  ];

  constructor(
    private orderService: OrderFacade,
    private myAccountService: MyAccountService,
    private orderMapper: OrderMapper,
    private cdRef: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: any
  ) {}

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.myAccountService
        .getProfil()
        .pipe(takeUntil(this.destroy$))
        .subscribe((userProfil) => {
          this.userName = userProfil.firstName;
          this.cdRef.markForCheck();
        });

      this.orderService
        .getOrderHistoryList(3, 0, undefined, this.LAST_ORDER_STATUSES)
        .pipe(
          tap((currentOrdersHistory) => {
            this.currentOrders = currentOrdersHistory.orders.map((order) =>
              this.orderMapper.orderHistoryToIOrderDetails(order)
            );
          }),
          switchMap((currentOrdersHistory): Observable<CustomOrder[]> => {
            if (currentOrdersHistory.orders.length > 0) {
              const ordersWithDetails$ = currentOrdersHistory.orders.map((order) =>
                this.orderService.getOrderDetails(order.code)
              );
              return combineLatest(ordersWithDetails$);
            } else {
              return of([]);
            }
          }),
          takeUntil(this.destroy$)
        )
        .subscribe((orders) => {
          this.currentOrders = orders.map((order) => this.orderMapper.orderToIOrderInformation(order));
          this.isLoading = false;
          this.cdRef.markForCheck();
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
